import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/config/reactQuery';
import { QUERY_KEYS } from '../../queryKeys';
import {
  CreateShippingManifestRequest,
  ManifestSearch,
  ShippingReferenceInput,
  UpdateShippingManifestRequest,
} from '@/types';
import { vessel } from '../Vessel';

export const useShippingManifests = (search: ManifestSearch) =>
  useQuery({
    queryKey: [QUERY_KEYS.shippingManifests, search],
    queryFn: async () => (await vessel.getShippingManifests(search)).data,
  });

export const useShippingManifest = (id?: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.shippingManifests, id],
    queryFn: async () => (await vessel.getShippingManifest(id as number)).data,
    enabled: Boolean(id),
  });

export const refreshShippingManifests = () => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.shippingManifests] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.vesselPositions] });
};

export const useCreateShippingManifest = () =>
  useMutation({
    mutationFn: (request: CreateShippingManifestRequest) =>
      vessel.createShippingManifest(request),
  });

export const useUpdateShippingManifest = () =>
  useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: UpdateShippingManifestRequest;
    }) => vessel.updateShippingManifest(id, body),
  });

export const useAddShippingManifestDocument = () =>
  useMutation({
    mutationFn: ({ id, file }: { id: number; file: FormData }) =>
      vessel.addShippingManifestDocument(id, file),
  });

export const useDeleteShippingManifestDocument = () =>
  useMutation({
    mutationFn: ({ id, documentId }: { id: number; documentId: number }) =>
      vessel.deleteShippingManifestDocument(id, documentId),
  });

export const useAddShippingManifestReference = () =>
  useMutation({
    mutationFn: ({ id, body }: { id: number; body: ShippingReferenceInput }) =>
      vessel.addShippingManifestReference(id, body),
  });

export const useUpdateShippingManifestReference = () =>
  useMutation({
    mutationFn: ({
      id,
      referenceId,
      body,
    }: {
      id: number;
      referenceId: number;
      body: ShippingReferenceInput;
    }) => vessel.updateShippingManifestReference(id, referenceId, body),
  });

export const useDeleteShippingManifestReference = () =>
  useMutation({
    mutationFn: ({ id, referenceId }: { id: number; referenceId: number }) =>
      vessel.deleteShippingManifestReference(id, referenceId),
  });

export const useDownloadDamageReport = () =>
  useMutation({
    mutationFn: ({
      id,
      format,
      scope,
      scopeValue,
    }: {
      id: number;
      format: string;
      scope: string;
      scopeValue?: string;
    }) => vessel.downloadDamageReport(id, format, scope, scopeValue),
  });
